import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { FaCubes, FaLeaf } from "react-icons/fa";
import { BiChevronRight } from "react-icons/bi";
import { BsExclamationTriangle } from "react-icons/bs";
import Modal from "@components/modal/Modal";
import TranslatableText from "@components/translations/TranslatableText";
import Button from "@components/button/Button";
import { useAppSelector } from "@app/hooks";
import { selectProjectAreaDefined } from "@features/project/projectSlice";
import type { TranslationKey } from "@typedefs/modulesInterfaces";

const CARD_CLASS =
	"d-flex align-items-center justify-content-between p-2 rounded-2 activity-type-card";
const CARD_CLASS_CLICKABLE = "clickable";
const CARD_STYLE = {
	border: "1px solid var(--gray)",
	backgroundColor: "white",
} as const;
const CARD_STYLE_DISABLED = {
	border: "1px solid var(--gray)",
	backgroundColor: "#f5f5f5",
} as const;
const ICON_WRAPPER_STYLE = {
	width: 40,
	height: 40,
	border: "1px solid var(--gray)",
} as const;

const ALERT_STYLE = {
	padding: "8px 12px",
	fontSize: "14px",
	backgroundColor: "#fff9e6",
	border: "1px solid #ffe8b3",
	borderRadius: "6px",
	color: "#b8860b",
} as const;

interface ActivityTypeOption {
	icon: ReactNode;
	iconBgColor: string;
	titleKey: TranslationKey;
	descriptionKey: TranslationKey;
	onSelect: () => void;
	disabled?: boolean;
	needsBaselineKey?: TranslationKey;
}

interface ActivityTypeCardProps {
	option: ActivityTypeOption;
	onKeySelect: (e: React.KeyboardEvent) => void;
}

const ActivityTypeCard = ({ option, onKeySelect }: ActivityTypeCardProps) => {
	const isDisabled = option.disabled ?? false;
	return (
		<Button
			type="button"
			tabIndex={isDisabled ? -1 : 0}
			onKeyDown={isDisabled ? undefined : (e) => onKeySelect(e)}
			onClick={isDisabled ? undefined : option.onSelect}
			className={`${CARD_CLASS} ${isDisabled ? "" : CARD_CLASS_CLICKABLE}`}
			style={isDisabled ? CARD_STYLE_DISABLED : CARD_STYLE}
			disabled={isDisabled}
		>
			<div className="d-flex align-items-center gap-2">
				<div
					className="d-flex align-items-center justify-content-center rounded-circle"
					style={{ ...ICON_WRAPPER_STYLE, backgroundColor: option.iconBgColor }}
				>
					{option.icon}
				</div>
				<div className="d-flex f-column ps-2 align-items-start">
					<span className={`fs-11 ff-medium ${isDisabled ? "text-gray" : "text-common-dark"}`}>
						<TranslatableText translationKey={option.titleKey} />
					</span>
					<span className="fs-11 text-gray">
						<TranslatableText translationKey={option.descriptionKey} />
					</span>
				</div>
			</div>
			{isDisabled && option.needsBaselineKey ? (
				<span className="fs-9" style={{ color: "#d97706" }}>
					<TranslatableText translationKey={option.needsBaselineKey} />
				</span>
			) : (
				<BiChevronRight size={24} color="var(--gray)" />
			)}
		</Button>
	);
};

interface NewActivityTypeModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSelectGHG: () => void;
	onSelectBiodiversity: () => void;
}

const NewActivityTypeModal = ({
	isOpen,
	onClose,
	onSelectGHG,
	onSelectBiodiversity,
}: NewActivityTypeModalProps) => {
	const projectAreaDefined = useAppSelector(selectProjectAreaDefined);

	const handleSelect = useCallback(
		(callback: () => void) => {
			onClose();
			callback();
		},
		[onClose],
	);

	const handleKeySelect = useCallback((e: React.KeyboardEvent, callback: () => void) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			callback();
		}
	}, []);

	const biodiversityDisabled = projectAreaDefined !== true;
	const options = useMemo<ActivityTypeOption[]>(
		() => [
			{
				icon: <FaCubes color="var(--bintact-bright, #4caf50)" size={20} />,
				iconBgColor: "var(--bintact-light-green, #e8f5e9)",
				titleKey: "activity.ghg_assessment",
				descriptionKey: "activity.ghg_assessment_description",
				onSelect: () => handleSelect(onSelectGHG),
			},
			{
				icon: <FaLeaf color="var(--bintact-bright)" size={20} />,
				iconBgColor: "var(--bintact-light, #fff3e0)",
				titleKey: "activity.biodiversity_assessment",
				descriptionKey: "activity.biodiversity_assessment_description",
				onSelect: () => handleSelect(onSelectBiodiversity),
				disabled: biodiversityDisabled,
				needsBaselineKey: biodiversityDisabled ? "activity.needs_baseline" : undefined,
			},
		],
		[handleSelect, onSelectGHG, onSelectBiodiversity, biodiversityDisabled],
	);

	const footerButtons = useMemo(
		() => [
			{
				type: "button" as const,
				onClick: onClose,
				children: <TranslatableText translationKey="main.cancel" />,
			},
		],
		[onClose],
	);

	if (!isOpen) return null;

	return (
		<Modal
			headerText="activity.new_activity"
			size="md"
			handleClose={onClose}
			footerButtons={footerButtons}
		>
			<div className="d-flex f-column gap-2 px-2 pb-2">
				<p className="fs-12 text-gray mb-2">
					<TranslatableText translationKey="activity.select_assessment_type" />
				</p>
				{options.map((option) => (
					<ActivityTypeCard
						key={option.titleKey}
						option={option}
						onKeySelect={(e) => handleKeySelect(e, option.onSelect)}
					/>
				))}
				{biodiversityDisabled && (
					<div className="d-flex align-items-center gap-2 mt-1" style={ALERT_STYLE}>
						<BsExclamationTriangle size={20} className="me-1" style={{ flexShrink: 0 }} />
						<TranslatableText
							translationKey="activity.biodiversity_requires_baseline_alert"
							paramsComponents={[<strong key="bold" />]}
						/>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default NewActivityTypeModal;
